import { motion } from "framer-motion";
import { BadgeCheck, FlaskConical, Microscope, Package, Warehouse, Beaker } from "lucide-react";

const GALLERY = [
  { src: "/plant/extraction-unit.webp", label: "Extraction & processing", icon: FlaskConical, span: "md:col-span-2 md:row-span-2" },
  { src: "/plant/raw-materials-storage.png", label: "Raw material store", icon: Beaker, span: "" },
  { src: "/plant/quality-lab.webp", label: "Quality control lab", icon: Microscope, span: "" },
  { src: "/plant/blending-unit.webp", label: "Blending & granulation", icon: Package, span: "" },
  { src: "/plant/warehouse-dispatch.webp", label: "Warehouse & dispatch", icon: Warehouse, span: "" },
];

const CERTS = ["WHO-GMP", "ISO 9001:2015", "ISO 22000", "FSSAI", "AYUSH Approved", "GMP Certified"];

export default function Manufacturing() {
  return (
    <section id="manufacturing" className="bg-[#f2ece4] py-20 md:py-28" data-testid="manufacturing-section">
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <div className="grid gap-10 lg:grid-cols-2 lg:items-end">
          <div>
            <div className="eyebrow text-[#d46a1d]">Manufacturing Excellence</div>
            <h2 className="mt-3 font-serif text-4xl tracking-tight text-[#202124] md:text-5xl">
              A facility built for scale, purity and speed
            </h2>
          </div>
          <p className="text-lg text-[#55534e]">
            Our own 50,000+ sq.ft. WHO-GMP manufacturing unit handles everything in-house — from raw-herb intake and standardised extraction to precision filling, packaging and dispatch. Full traceability at every step.
          </p>
        </div>

        {/* Bento gallery */}
        <div className="mt-12 grid grid-cols-1 gap-4 md:grid-cols-4 md:grid-rows-2">
          {GALLERY.map((g, i) => {
            const Icon = g.icon;
            return (
              <motion.div key={i}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ duration: 0.55, delay: i * 0.07 }}
                className={`group relative overflow-hidden rounded-2xl ${g.span}`} data-testid={`facility-tile-${i}`}>
                <img src={g.src} alt={g.label} loading="lazy"
                  className="h-full min-h-[180px] w-full object-cover object-center transition-transform duration-700 group-hover:scale-105"
                  style={{ objectPosition: "center 62%" }} />
                <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/5 to-transparent" />
                <div className="absolute bottom-4 left-4 inline-flex items-center gap-2 rounded-full bg-white/95 px-3 py-1.5">
                  <Icon size={15} className="text-[#d46a1d]" />
                  <span className="text-xs font-semibold text-[#202124]">{g.label}</span>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Certifications */}
        <div className="mt-12 rounded-2xl border border-[#e7e0d6] bg-white p-8 card-shadow">
          <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
            <div className="flex items-center gap-3">
              <BadgeCheck size={22} className="text-[#1f5d3a]" />
              <span className="font-serif text-2xl text-[#202124]">Certified & compliant</span>
            </div>
            <div className="flex flex-wrap gap-3">
              {CERTS.map((c) => (
                <span key={c} data-testid={`cert-${c}`}
                  className="rounded-full border border-[#d8cfc1] bg-[#f9f8f5] px-4 py-2 text-sm font-semibold text-[#1f5d3a]">
                  {c}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
